import * as React from 'react';
import { applyClassName, styled } from '@glitz/react';
import {
  ProgressiveHydration,
  useIsCurrentPageType,
  useMatchLargeViewport,
  useMatchSmallViewport,
  useStaticData,
} from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { ArrowRight } from 'webapp/components/icons';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { Block, maxContentWideWidthDecorator } from 'webapp/components/page-layout';
import Link from 'webapp/components/Link';
import Countdown from 'webapp/shared/Countdown';

export default React.memo(function SiteWideBanner() {
  const siteWideBanner = useStaticData<NordicNest.Api.SiteWideBannerApiModel>();
  const countdown = siteWideBanner?.message?.countdown;
  const backgroundColor = siteWideBanner?.backgroundColor;
  const textColor = siteWideBanner?.textColor;

  const backgroundDecorator = styled({
    backgroundColor: backgroundColor,
    color: textColor,
    ':hover': {
      color: textColor,
    },
  });

  const messageContent = siteWideBanner ? (
    <Document
      styleDecorators={styles}
      {...siteWideBanner?.message.content}
      args={{ countdown: <Countdown countdown={siteWideBanner?.message.countdown} /> }}
    />
  ) : null;

  const isExpressCheckout = useIsCurrentPageType<NordicNest.Api.ExpressCheckoutPageApiModel>();
  const isLargeViewport = useMatchLargeViewport();
  const showBanner = !isExpressCheckout || isLargeViewport;
  return siteWideBanner && siteWideBanner.message?.content && showBanner ? (
    <ProgressiveHydration skipHydration={!countdown}>
      <Banner css={backgroundDecorator}>
        <Messages>
          {siteWideBanner.message.path ? (
            <Message to={siteWideBanner.message.path}>
              {messageContent}
              {siteWideBanner.message.path && <Arrow />}
            </Message>
          ) : (
            messageContent
          )}
        </Messages>
      </Banner>
    </ProgressiveHydration>
  ) : null;
});

const Banner = styled.div({
  padding: { x: t => t.spacing.xs, y: t => t.spacing.xxs },
});

const Messages = styled(Block, maxContentWideWidthDecorator, {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'center',
  height: 'inherit',
  transition: {
    property: 'opacity',
    duration: 200,
    timingFunction: 'ease-in',
  },
});

const Message = styled(applyClassName(Link), {
  fontSize: t => t.sharedFontSize.xs,
  textDecorationLine: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: t => t.spacing.xs,
  ':hover': {
    textDecorationLine: 'underline',
  },
});

const Arrow = styled(ArrowRight, {
  fontSize: t => t.fontSize.xxs,
  flexShrink: 0,
});

const noMargin = styled(
  createMinLargeViewportDecorator({ marginBottom: 0 }),
  createMaxSmallViewportDecorator({ marginBottom: 0, textAlign: 'center' }),
);
const styles: StyleDecorators = {
  p: noMargin,
  h1: noMargin,
  h2: noMargin,
  h3: noMargin,
  h4: noMargin,
  h5: noMargin,
  h6: noMargin,
};
